// Must match MAX_IMAGES_PER_REQUEST in filter-ai/app/api/image_analysis_routes.py.
//
// Mirrored rather than fetched because it gates the BUTTON — the user has to be told "25 at a time" before
// they press it, not after a 422 comes back. The backend stays the enforcer; this is only the label.
export const MAX_IMAGES_PER_ANALYSIS = 25;

// Per-image outcomes the backend can report. Anything not `ok` is a reason the photograph produced no
// reading, and each one means something different to the person looking at it — which is why they are
// rendered individually rather than collapsed into "failed".
export const RESULT_STATUS = {
    OK: "ok",
    NOT_FOUND: "not_found",
    NO_KEY: "no_key",
    NOT_SIGNABLE: "not_signable",
    UNREADABLE: "unreadable",
    UNDECODABLE: "undecodable",
    UNSUPPORTED: "unsupported",
    NO_IMAGING: "no_imaging",
    FAILED: "failed"
};

export const STATUS_LABEL = {
    [RESULT_STATUS.NOT_FOUND]: "Image not found",
    [RESULT_STATUS.NO_KEY]: "No stored file for this image",
    [RESULT_STATUS.NOT_SIGNABLE]: "Image storage is not configured",
    [RESULT_STATUS.UNREADABLE]: "Could not download the image",
    [RESULT_STATUS.UNDECODABLE]: "Could not read the image file",
    [RESULT_STATUS.UNSUPPORTED]: "The model could not process this image",
    // Not about this photograph at all - the server cannot decode ANY image (its imaging library is
    // missing). Worded so nobody goes looking at the file: the first time this happened, in Dev3, it read
    // as "could not read the image file" and sent people to the photographs.
    [RESULT_STATUS.NO_IMAGING]: "Image processing is unavailable on the server",
    [RESULT_STATUS.FAILED]: "Analysis failed"
};

// Why the backend refused the whole request, rather than any one photograph. Both mean a server that is
// not set up, and both are worth saying plainly - "nothing happened" on a button that spends money reads
// as a bug in the page.
export const UNAVAILABLE_REASON = {
    s3_not_configured: "Image storage is not configured on the server, so Otto cannot read the photo.",
    imaging_not_available: "Image processing is unavailable on the server, so no photo can be analysed."
};

export const SEVERITY_CLASS = {
    minor: "ia-sev-minor",
    moderate: "ia-sev-moderate",
    severe: "ia-sev-severe"
};

// Presentation only. The backend's `setting` / `space_type` / `quality` values are snake_case vocabulary
// terms; this turns them into something a surveyor reads without translating.
export const humanise = value =>
    !value ? "" : String(value).replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());

// Precision follows magnitude. A single photograph costs about $0.0015, and a fixed two decimals rendered
// that as "$0.00" - which reads as free, on the one screen whose job is to say what something costs.
export const money = n => {
    const v = Number(n) || 0;
    if (v >= 10) return `$${v.toFixed(0)}`;
    if (v >= 1) return `$${v.toFixed(2)}`;
    if (v > 0) return `$${v.toFixed(4)}`;
    return "$0.00";
};

// A price per photograph is four decimal places of noise to read; the same number per thousand photographs
// is what anyone choosing between two models actually wants ("$1.40 / 1,000" against "$0.98 / 1,000"). Two
// decimals from a cent up, rather than `money`'s magnitude rule, so the two models line up where they are
// read side by side - `money` renders that same pair "$1.40" and "$0.9770".
export const perThousand = n => {
    const v = (Number(n) || 0) * 1000;
    return `${v >= 0.01 ? `$${v.toFixed(2)}` : money(v)} / 1,000 images`;
};

// A response that a curator's gate stripped has no cost fields at all, so every price is drawn as "show it
// if we HAVE it", never "hide it if not allowed" - the two read the same on screen and fail differently.
export const hasNumber = n => typeof n === "number" && !Number.isNaN(n);
